import {
  AttachmentCategoryEnum,
  type MbcBriefAssetTarget,
  ModelsEnum,
} from "@nlc/shared-types";
import { useEffect, useState } from "react";

import { FileUploadDropZone } from "@/components/file-uploader/components/FileUploadDropZone";
import { FileUploaderProvider } from "@/components/file-uploader/FileUploaderContext";
import { useFileUploaderContext } from "@/components/file-uploader/UseFileUploaderContext";
import { MediaPreviewModalProvider } from "@/components/media-preview-modal/MediaPreviewModalProvider";
import { useWhitelabel } from "@/hooks/useWhitelabel";

import type { AssetFileRef, AssetGroup, AssetGroupChange } from "./types";

import { useDeleteAsset } from "../../hooks/useDeleteAsset";
import { useFilePreview } from "../../hooks/useFilePreview";
import { ASSET_COPY } from "./asset-copy";
import { AssetDropzone } from "./AssetDropzone";
import { AssetLinkInput } from "./AssetLinkInput";
import { AssetLinkRow } from "./AssetLinkRow";
import { AssetTileGrid } from "./AssetTileGrid";
import { DeleteAllFiles } from "./DeleteAllFiles";

type AssetUploadBlockProps = {
  onChange: (change: AssetGroupChange) => void;
  /** Re-judge the target's grade after one of its files was uploaded or deleted. */
  onFilesChanged: (target: MbcBriefAssetTarget) => void;
  /** Reports +1 when an upload starts and -1 when it settles. */
  onUploadingChange: (delta: -1 | 1) => void;
  /** Without a request there is nothing to upload to or edit: the block is read-only. */
  requestId: null | number;
  target: MbcBriefAssetTarget;
  /** Section name, shown in the "all files" modal. */
  title: string;
  value: AssetGroup;
};

type FilesGridProps = {
  files: AssetFileRef[];
  isEditable: boolean;
  onPreview: (attachmentId: number) => void;
  onRemoveFile: (attachmentId: number) => void;
  onUploadingChange: (delta: -1 | 1) => void;
  target: MbcBriefAssetTarget;
  title: string;
};

/** Reads the uploader to show in-progress and failed uploads; finished ones come from the brief. */
function UploaderFilesGrid({
  onUploadingChange,
  target,
  ...gridProps
}: FilesGridProps) {
  const { files, isUploading, removeFile, retryUpload } =
    useFileUploaderContext();

  useEffect(() => {
    if (!isUploading) return;

    onUploadingChange(1);

    return () => onUploadingChange(-1);
  }, [isUploading, onUploadingChange]);

  const unfinishedUploads = files.filter((file) => file.status !== "success");

  if (gridProps.files.length === 0 && unfinishedUploads.length === 0) {
    return <AssetDropzone target={target} />;
  }

  return (
    <AssetTileGrid
      {...gridProps}
      onRemoveUpload={removeFile}
      onRetryUpload={retryUpload}
      unfinishedUploads={unfinishedUploads}
    />
  );
}

const PANEL_CLASS =
  "rounded-2xl border bg-bg-secondary p-[14px] transition-colors sm:p-2xl";

export function AssetUploadBlock(props: AssetUploadBlockProps) {
  return (
    <MediaPreviewModalProvider>
      <AssetUploadBlockContent {...props} />
    </MediaPreviewModalProvider>
  );
}

function AssetUploadBlockContent({
  onChange,
  onFilesChanged,
  onUploadingChange,
  requestId,
  target,
  title,
  value,
}: AssetUploadBlockProps) {
  const [linkError, setLinkError] = useState<string>();

  const removeFile = useDeleteAsset(() => onFilesChanged(target));
  const previewFile = useFilePreview(value.files);

  const addLink = (url: string): boolean => {
    const normalized = /^https?:\/\//i.test(url) ? url : `https://${url}`;

    try {
      new URL(normalized);
    } catch {
      setLinkError("Please enter a valid URL (e.g., https://example.com).");

      return false;
    }

    setLinkError(undefined);
    if (!value.links.some((link) => link.url === normalized)) {
      onChange({ addLinks: [normalized] });
    }

    return true;
  };

  const removeLink = (url: string) => onChange({ removeLinks: [url] });

  const isEditable = requestId !== null;

  const { isWhitelabel } = useWhitelabel();

  const gridProps = {
    files: value.files,
    isEditable,
    onPreview: previewFile,
    onRemoveFile: removeFile,
    title,
  };

  const itemCount = value.files.length + value.links.length;
  const { linkPlaceholder, scope } = ASSET_COPY[target];

  const content = (
    <div className="flex flex-col gap-2xl">
      {isEditable || value.files.length > 0 ? (
        <div className="flex flex-col gap-[14px]">
          {itemCount > 0 ? (
            <div className="flex items-center justify-between">
              <b className="text-[15px] font-semibold text-text-primary">
                {itemCount} {itemCount === 1 ? "item" : "items"} in {scope}
              </b>
              {isEditable ? (
                <DeleteAllFiles
                  files={value.files}
                  linkCount={value.links.length}
                  onDeleted={() => onFilesChanged(target)}
                  onRemoveLinks={() =>
                    onChange({ removeLinks: value.links.map(({ url }) => url) })
                  }
                  scope={scope}
                />
              ) : null}
            </div>
          ) : null}
          {isEditable ? (
            <UploaderFilesGrid
              {...gridProps}
              onUploadingChange={onUploadingChange}
              target={target}
            />
          ) : (
            <AssetTileGrid {...gridProps} />
          )}
        </div>
      ) : null}

      {isEditable || value.links.length > 0 ? (
        <div className="flex flex-col gap-lg">
          {isEditable ? (
            <AssetLinkInput
              error={linkError}
              isWhitelabel={isWhitelabel}
              onAdd={addLink}
              placeholder={linkPlaceholder}
            />
          ) : null}
          {value.links.length > 0 ? (
            <ul className="flex flex-col gap-md">
              {value.links.map((link) => (
                <AssetLinkRow
                  key={link.url}
                  onDelete={isEditable ? removeLink : undefined}
                  showShareHint={!isWhitelabel}
                  url={link.url}
                />
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );

  if (!isEditable) {
    return itemCount > 0 ? (
      <div className={`${PANEL_CLASS} border-border-tertiary`}>{content}</div>
    ) : null;
  }

  return (
    <FileUploaderProvider
      category={
        target === "inspirations"
          ? AttachmentCategoryEnum.EXAMPLE
          : AttachmentCategoryEnum.ASSET
      }
      isMbc
      onFileUploaded={() => onFilesChanged(target)}
      parentId={requestId}
      parentType={ModelsEnum.REQUEST}
    >
      <FileUploadDropZone
        className={({ isDropTarget }) =>
          `group/panel ${PANEL_CLASS} ${
            isDropTarget
              ? "border-border-brand-solid"
              : "border-border-tertiary"
          }`
        }
      >
        {content}
      </FileUploadDropZone>
    </FileUploaderProvider>
  );
}
